import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRightLeft, Plus, Undo2 } from "lucide-react";
import { getVzOwnership, undoVzOwnership } from "@/lib/verizonSalesApi";
import { OwnershipChangeDialog } from "@/components/verizon/OwnershipChangeDialog";

const fromCandidate = (c, cur) => {
  const old = c.tsps[0];
  const nw = c.tsps[c.tsps.length - 1];
  return { location_id: (old.location || nw.location)?.id, old_tsp: old.door_tsp, new_tsp: nw.door_tsp, effective_month: nw.first_month || cur };
};

export const OwnershipChanges = ({ stores, locations, onChanged }) => {
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => getVzOwnership().then(setData).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged(); };
  const undo = async (c) => {
    try { await undoVzOwnership(c.id); toast.success(`Undid ownership change for ${c.store_id}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
  };
  if (!data) return null;

  return (
    <Card className="border-slate-200" data-testid="vz-ownership-card">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-teal" /> Ownership changes</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setDlg({ effective_month: data.current_month })} data-testid="vz-own-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Link additional TSP / ownership change
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {data.candidates.map((c) => (
          <div key={c.store_id} className="border border-amber-200 bg-amber-50 rounded-lg p-2 flex items-center gap-2 flex-wrap" data-testid={`vz-own-candidate-${c.store_id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-navy">{c.store_id} · {c.address}, {c.city} has {c.tsps.length} door TSPs</div>
              {c.tsps.map((t) => (
                <div key={t.door_tsp} className="text-slate-600">TSP {t.door_tsp} · {t.sub_agent_name} · since {t.first_month || "?"} · {t.current_acts ?? 0} acts</div>
              ))}
            </div>
            <Button size="sm" onClick={() => setDlg(fromCandidate(c, data.current_month))} data-testid={`vz-own-resolve-${c.store_id}`}>Record ownership change</Button>
          </div>
        ))}
        {data.changes.map((c) => (
          <div key={c.id} className="border border-slate-100 rounded-lg p-2 flex items-center gap-2 flex-wrap" data-testid={`vz-own-change-${c.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-navy">{c.store_id} · {c.location_name}</div>
              <div className="text-slate-600">TSP {c.door_tsp} ({c.old_sub_agent_name || "previous owner"}) through {c.end_month} → TSP {c.new_tsp} from {c.effective_month}</div>
              <div className="text-slate-400">History kept for: {(c.dealer_names || []).join(", ") || "admins only"}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => undo(c)} data-testid={`vz-own-undo-${c.id}`}><Undo2 className="w-4 h-4 mr-1" /> Undo</Button>
          </div>
        ))}
        {data.candidates.length === 0 && data.changes.length === 0 && <p className="text-slate-500">No ownership changes detected.</p>}
      </CardContent>
      <OwnershipChangeDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} initial={dlg || {}} stores={stores}
        locations={locations} dealers={data.dealers} onSaved={refresh} />
    </Card>
  );
};
